/**
 * Widget V2 - URL Utilities
 *
 * Deep linking and URL parameter synchronization.
 */

import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { DateTime } from "luxon";

const DEFAULT_CLUB_TZ = "Asia/Dubai";

/**
 * Parse URL search params into an object
 */
export function parseSearchParams(searchParams) {
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    // Handle array values (key=value1&key=value2)
    if (params[key]) {
      if (Array.isArray(params[key])) {
        params[key].push(value);
      } else {
        params[key] = [params[key], value];
      }
    } else {
      params[key] = value;
    }
  }
  return params;
}

/**
 * A "where I came from" path, only if it really is one inside this app.
 *
 * These arrive in the URL, so anything is possible: an absolute URL would turn
 * a way back into an open redirect, and a protocol-relative `//host` reads as
 * one too. Anything else is treated as absent, which just costs the client the
 * shortcut back.
 */
export function internalPath(value) {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

/**
 * Build search params string from object
 */
export function buildSearchParams(params) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined && v !== "") {
          searchParams.append(key, v);
        }
      });
    } else {
      searchParams.set(key, value);
    }
  });

  return searchParams.toString();
}

/**
 * Hook for managing URL filters
 *
 * @param {Object} defaultValues - Default filter values
 * @returns {Object} - { filters, setFilter, setFilters, clearFilters }
 */
export function useUrlFilters(defaultValues = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // A filter tweak replaces the same history entry, so it keeps that entry's
  // state (e.g. the dashboard's "opened from the menu" marker).
  const { state: locationState } = useLocation();

  // Parse current filters from URL
  const filters = useMemo(() => {
    const parsed = parseSearchParams(searchParams);

    // Apply defaults for missing values
    return {
      ...defaultValues,
      ...parsed
    };
  }, [searchParams, defaultValues]);

  // Set a single filter
  const setFilter = useCallback(
    (key, value) => {
      const newParams = new URLSearchParams(searchParams);

      if (value === null || value === undefined || value === "") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }

      setSearchParams(newParams, { replace: true, state: locationState });
    },
    [searchParams, setSearchParams, locationState]
  );

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFilters) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      setSearchParams(newParams, { replace: true, state: locationState });
    },
    [searchParams, setSearchParams, locationState]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters
  };
}

/**
 * Hook for navigation with preserved filters
 */
export function useNavigateWithFilters() {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateWithFilters = useCallback(
    (path, options = {}) => {
      const { preserveFilters = false, filters = {}, ...navOptions } = options;

      let search = "";

      if (preserveFilters) {
        // Preserve current search params
        const currentParams = new URLSearchParams(location.search);
        const merged = new URLSearchParams(currentParams);

        // Apply any new filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") {
            merged.delete(key);
          } else {
            merged.set(key, value);
          }
        });

        search = merged.toString();
      } else if (Object.keys(filters).length > 0) {
        search = buildSearchParams(filters);
      }

      const fullPath = search ? `${path}?${search}` : path;
      navigate(fullPath, navOptions);
    },
    [navigate, location.search]
  );

  return navigateWithFilters;
}

/**
 * Build a shareable URL for current state
 */
export function buildShareableUrl(slug, view, filters = {}) {
  const baseUrl = window.location.origin;
  const path = view ? `/${slug}/${view}` : `/${slug}`;
  const search = buildSearchParams(filters);

  return search ? `${baseUrl}${path}?${search}` : `${baseUrl}${path}`;
}

/**
 * Parse widget URL to extract slug and initial state
 */
export function parseWidgetUrl(url) {
  try {
    const urlObj = new URL(url, window.location.origin);
    const pathParts = urlObj.pathname.split("/").filter(Boolean);

    if (pathParts.length === 0) {
      return null;
    }

    return {
      slug: pathParts[0],
      view: pathParts[1] || null,
      subView: pathParts[2] || null,
      filters: parseSearchParams(urlObj.searchParams)
    };
  } catch (err) {
    return null;
  }
}

/**
 * Date formatting utilities for URLs
 */
export const dateUtils = {
  /**
   * Format date for URL (YYYY-MM-DD)
   */
  toUrlDate(date) {
    if (!date) return null;
    if (typeof date === "string") return date;

    const d = new Date(date);
    return d.toISOString().split("T")[0];
  },

  /**
   * Parse URL date string to Date object
   */
  fromUrlDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr + "T00:00:00");
  },

  /**
   * Get today's date formatted for URL
   */
  today() {
    return new Date().toISOString().split("T")[0];
  },

  /**
   * Today's date in the club's timezone. `today()` is the UTC calendar date,
   * which stays on yesterday until 04:00 in Dubai.
   */
  clubToday(timeZone) {
    const now = DateTime.now().setZone(timeZone || DEFAULT_CLUB_TZ);
    return (now.isValid ? now : DateTime.now().setZone(DEFAULT_CLUB_TZ)).toISODate();
  },

  /**
   * Get week start date (Monday) for a given date
   */
  weekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split("T")[0];
  }
};

export default {
  parseSearchParams,
  buildSearchParams,
  useUrlFilters,
  useNavigateWithFilters,
  buildShareableUrl,
  parseWidgetUrl,
  dateUtils
};
